import { Link, useParams } from 'react-router';
import { useLearner } from '@/app/useAppState';
import { NotFound } from '@/app/RouteError';
import { SampleBadge, STAGE, StatusChip, SubjectBadge } from '@/components/ui/Badges';
import { Loading } from '@/components/ui/Loading';
import { Notice } from '@/components/ui/Notice';
import { ScreenHeader } from '@/components/ui/ScreenHeader';
import { curriculum } from '@/content/curriculum';
import type { LearningObjective } from '@/content/schema';
import type { MasteryRecord } from '@/domain/records';
import { useMastery, useRecommendations } from '@/features/learning/hooks';
import { useI18n } from '@/i18n/context';
import { cn } from '@/lib/cn';
import { rulesOf } from './rules';

function ObjectiveNode({
  objective,
  record,
  recommended,
  index,
}: {
  objective: LearningObjective;
  record?: MasteryRecord;
  recommended: boolean;
  index: number;
}) {
  const { t, l } = useI18n();
  const status = record?.status ?? 'notStarted';
  return (
    <li className={cn('relative flex', index % 2 === 1 ? 'sm:justify-end' : 'sm:justify-start')}>
      <Link
        to={`/learn/objectives/${objective.id}`}
        className={cn(
          'flex w-full items-center gap-4 rounded-[var(--radius-card)] bg-surface p-4 shadow-[var(--shadow-card)] transition-transform hover:-translate-y-0.5 sm:w-[85%]',
          recommended && 'ring-4 ring-sun-300',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'emoji flex size-14 shrink-0 items-center justify-center rounded-full text-3xl',
            STAGE[status].tone,
          )}
        >
          {STAGE[status].emoji}
        </span>
        <span className="flex min-w-0 flex-col gap-1">
          <span className="font-extrabold">{l(objective.title)}</span>
          <StatusChip status={status} className="self-start" />
        </span>
        {recommended && (
          <span className="ms-auto shrink-0 rounded-full bg-sun-300 px-3 py-1 text-xs font-extrabold text-ink">
            {t('lesson.recommended')}
          </span>
        )}
      </Link>
    </li>
  );
}

export function LearningMapScreen() {
  const { subjectId = '' } = useParams();
  const learner = useLearner();
  const { t, l } = useI18n();
  const mastery = useMastery(learner.id);
  const recommendations = useRecommendations(learner, subjectId);
  const subject = curriculum.subject(subjectId);
  if (!subject || !subject.levels.includes(learner.levelId)) return <NotFound />;
  if (!mastery || !recommendations) return <Loading label={t('app.loading')} />;

  const objectives = curriculum.objectivesFor(subject.id, learner.levelId, learner.variantId);
  const earlier = curriculum.earlierObjectives(subject.id, learner.levelId, learner.variantId);
  const recommendedId = recommendations[0]?.objectiveId;
  const ruleCount = rulesOf([...earlier, ...objectives]).length;

  return (
    <div className={cn(`subject-${subject.color}`, 'flex flex-col gap-6')}>
      <div className="flex items-center gap-3">
        <SubjectBadge subject={subject} size="lg" />
        <ScreenHeader
          title={l(subject.name)}
          subtitle={t('map.title')}
          back="/learn/subjects"
          className="flex-1"
        />
      </div>
      {subject.availabilityNote && (
        <Notice tone="warning" icon="🧪">
          {l(subject.availabilityNote)}
        </Notice>
      )}
      {ruleCount > 0 && (
        <Link
          to={`/learn/subjects/${subject.id}/rules`}
          className="flex items-center gap-3 self-start rounded-full border-2 border-[var(--subject)] bg-[var(--subject-soft)] px-4 py-2 font-extrabold text-[var(--subject-ink)] hover:bg-surface"
        >
          <span aria-hidden className="emoji">
            📌
          </span>
          {t('rules.link', { count: ruleCount })}
        </Link>
      )}
      {objectives.length === 0 ? (
        <p className="text-ink-muted">{t('map.empty')}</p>
      ) : (
        <ol className="flex flex-col gap-4">
          {objectives.map((objective, i) => (
            <ObjectiveNode
              key={objective.id}
              objective={objective}
              record={mastery.get(objective.id)}
              recommended={objective.id === recommendedId}
              index={i}
            />
          ))}
        </ol>
      )}
      {earlier.length > 0 && (
        <details className="rounded-[var(--radius-card)] bg-surface-muted p-4">
          <summary className="cursor-pointer font-extrabold">{t('map.earlier')}</summary>
          <ol className="mt-4 flex flex-col gap-3">
            {earlier.map((objective, i) => (
              <ObjectiveNode
                key={objective.id}
                objective={objective}
                record={mastery.get(objective.id)}
                recommended={false}
                index={i}
              />
            ))}
          </ol>
        </details>
      )}
      <SampleBadge className="self-start" />
    </div>
  );
}
